import { type FormEvent, useEffect, useMemo, useState } from 'react';
import {
  archiveCompany,
  archiveProject,
  assignProjectUser,
  confirmProjectCompletion,
  createCatalogItem,
  createCompany,
  createProject,
  getProject,
  inviteCompanyUser,
  listCatalog,
  listCompanies,
  listCompanyUsers,
  listProjectAssignments,
  listProjects,
  removeCompanyUser,
  requestProjectCompletion,
  unassignProjectUser,
  updateCatalogItem,
  updateCompany,
  updateCompanyUser,
  updateProject,
  type SessionResponse,
  type CatalogItem,
  type Company,
  type CompanyInput,
  type CompanyUser,
  type Project,
  type ProjectAssignment,
  type ProjectInput,
  type User,
} from './api';
import { AccountSettingsPanel } from './AccountSettingsPanel';
import { ContentLibraryPanel } from './ContentLibraryPanel';
import { ElectronicDeliveryProfilesPanel } from './ElectronicDeliveryProfilesPanel';
import { TrainingRequestsPanel } from './TrainingRequestsPanel';
import { DeliveryPortalPanel } from './DeliveryPortalPanel';
import { ProjectArchivePanel } from './ProjectArchivePanel';
import { RetentionPanel } from './RetentionPanel';

type Props = {
  csrfToken: string;
  onAccountUpdated: (session: SessionResponse) => void;
  onError: (error: unknown) => void;
  onLogout: () => void;
  user: User;
};

type Tab =
  | 'company'
  | 'content'
  | 'delivery'
  | 'electronic-profiles'
  | 'masters'
  | 'projects'
  | 'requests'
  | 'users';

const today = new Date().toISOString().slice(0, 10);

const emptyCompany: CompanyInput = {
  companyCode: '',
  contractStartedOn: today,
  name: '',
  status: 'ACTIVE',
};

const emptyProject: ProjectInput = {
  clientProfileType: 'HOKKAIDO_DEVELOPMENT_BUREAU',
  equipmentTypeIds: [],
  name: '',
  plannedCompleteOn: today,
  plannedStartOn: today,
  projectCode: '',
  status: 'PLANNED',
  workTypeIds: [],
};

function labelForClient(project: Project): string {
  if (project.clientProfileType === 'HOKKAIDO_DEVELOPMENT_BUREAU') return '北海道開発局';
  if (project.clientProfileType === 'HOKKAIDO_CONSTRUCTION_DEPARTMENT') return '北海道建設部';
  return project.municipalityName ?? '市町村';
}

function nullable(value: FormDataEntryValue | null): string | null {
  if (typeof value !== 'string') return null;
  const text = value.trim();
  return text.length === 0 ? null : text;
}

function formText(data: FormData, name: string): string {
  const value = data.get(name);
  return typeof value === 'string' ? value : '';
}

export function OperationsDashboard({
  csrfToken,
  onAccountUpdated,
  onError,
  onLogout,
  user,
}: Props) {
  const [companies, setCompanies] = useState<Company[]>([]);
  const [selectedCompanyId, setSelectedCompanyId] = useState('');
  const [projects, setProjects] = useState<Project[]>([]);
  const [users, setUsers] = useState<CompanyUser[]>([]);
  const [workTypes, setWorkTypes] = useState<CatalogItem[]>([]);
  const [equipmentTypes, setEquipmentTypes] = useState<CatalogItem[]>([]);
  const [tab, setTab] = useState<Tab>('projects');
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [companyDraft, setCompanyDraft] = useState<CompanyInput>(emptyCompany);
  const [projectDraft, setProjectDraft] = useState<ProjectInput>(emptyProject);
  const [editingProject, setEditingProject] = useState<Project | null>(null);
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);
  const [assignments, setAssignments] = useState<ProjectAssignment[]>([]);
  const [invitationToken, setInvitationToken] = useState<string | null>(null);
  const [accountSettingsOpen, setAccountSettingsOpen] = useState(false);

  const selectedCompany = useMemo(
    () => companies.find((company) => company.id === selectedCompanyId) ?? null,
    [companies, selectedCompanyId],
  );
  const superAdmin = user.globalRole === 'SUPER_ADMIN';
  const canManageCompany = superAdmin || selectedCompany?.currentRole === 'COMPANY_ADMIN';

  async function refreshCompanies(preferredId?: string) {
    const next = await listCompanies();
    setCompanies(next);
    setSelectedCompanyId((current) => {
      const candidate = preferredId ?? current;
      return next.some((company) => company.id === candidate) ? candidate : (next[0]?.id ?? '');
    });
  }

  async function refreshCompanyData(companyId: string) {
    if (companyId === '') {
      setProjects([]);
      setUsers([]);
      return;
    }
    const company = companies.find((item) => item.id === companyId);
    const canManage = superAdmin || company?.currentRole === 'COMPANY_ADMIN';
    const [nextProjects, nextUsers] = await Promise.all([
      listProjects(companyId),
      canManage ? listCompanyUsers(companyId) : Promise.resolve([]),
    ]);
    setProjects(nextProjects);
    setUsers(nextUsers);
    setSelectedProject((current) =>
      current === null ? null : (nextProjects.find((item) => item.id === current.id) ?? null),
    );
  }

  useEffect(() => {
    void Promise.all([listCatalog('work-types'), listCatalog('equipment-types'), listCompanies()])
      .then(([nextWorkTypes, nextEquipmentTypes, nextCompanies]) => {
        setWorkTypes(nextWorkTypes);
        setEquipmentTypes(nextEquipmentTypes);
        setCompanies(nextCompanies);
        setSelectedCompanyId(nextCompanies[0]?.id ?? '');
      })
      .catch(onError);
  }, [onError]);

  useEffect(() => {
    void refreshCompanyData(selectedCompanyId).catch(onError);
    setSelectedProject(null);
    setAssignments([]);
    setEditingProject(null);
    setProjectDraft(emptyProject);
  }, [selectedCompanyId]);

  async function run(action: () => Promise<void>, success: string) {
    setBusy(true);
    setNotice(null);
    try {
      await action();
      setNotice(success);
    } catch (error) {
      onError(error);
    } finally {
      setBusy(false);
    }
  }

  async function submitCompany(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    await run(
      async () => {
        if (selectedCompany !== null) {
          const updated = await updateCompany(
            selectedCompany.id,
            { ...companyDraft, version: selectedCompany.version },
            csrfToken,
          );
          await refreshCompanies(updated.id);
        } else {
          const created = await createCompany(companyDraft, csrfToken);
          await refreshCompanies(created.id);
        }
      },
      selectedCompany === null ? '企業を登録しました。' : '企業情報を更新しました。',
    );
  }

  function beginCompany(company: Company | null) {
    setCompanyDraft(
      company === null
        ? emptyCompany
        : {
            address: company.address,
            companyCode: company.companyCode,
            contactEmail: company.contactEmail,
            contactName: company.contactName,
            contractStartedOn: company.contractStartedOn,
            name: company.name,
            nameKana: company.nameKana,
            phone: company.phone,
            remarks: company.remarks,
            status: company.status,
          },
    );
    if (company === null) setSelectedCompanyId('');
    else setSelectedCompanyId(company.id);
    setTab('company');
  }

  async function submitProject(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (selectedCompany === null) return;
    await run(
      async () => {
        const project =
          editingProject === null
            ? await createProject(selectedCompany.id, projectDraft, csrfToken)
            : await updateProject(
                selectedCompany.id,
                editingProject.id,
                { ...projectDraft, version: editingProject.version },
                csrfToken,
              );
        setEditingProject(null);
        setProjectDraft(emptyProject);
        await refreshCompanyData(selectedCompany.id);
        await selectProject(project.id);
      },
      editingProject === null ? '工事を登録しました。' : '工事情報を更新しました。',
    );
  }

  async function beginProjectEdit(project: Project) {
    if (selectedCompany === null) return;
    try {
      const detail = await getProject(selectedCompany.id, project.id);
      setEditingProject(detail);
      setProjectDraft({
        chiefEngineerName: detail.chiefEngineerName,
        clientProfileType: detail.clientProfileType,
        contractNumber: detail.contractNumber,
        equipmentTypeIds: detail.equipmentTypeIds ?? [],
        expectedTrainingCount: detail.expectedTrainingCount,
        mainWorkSummary: detail.mainWorkSummary,
        municipalityName: detail.municipalityName,
        name: detail.name,
        plannedCompleteOn: detail.plannedCompleteOn,
        plannedStartOn: detail.plannedStartOn,
        projectCode: detail.projectCode,
        remarks: detail.remarks,
        siteManagerName: detail.siteManagerName,
        status:
          detail.status === 'PLANNED' || detail.status === 'SUSPENDED' ? detail.status : 'ACTIVE',
        supervisingEngineerName: detail.supervisingEngineerName,
        workTypeIds: detail.workTypeIds ?? [],
      });
    } catch (error) {
      onError(error);
    }
  }

  async function selectProject(projectId: string) {
    if (selectedCompany === null) return;
    try {
      const detail = await getProject(selectedCompany.id, projectId);
      setSelectedProject(detail);
      if (canManageCompany) {
        setAssignments(await listProjectAssignments(selectedCompany.id, projectId));
      }
    } catch (error) {
      onError(error);
    }
  }

  function toggleSelection(field: 'equipmentTypeIds' | 'workTypeIds', id: string) {
    setProjectDraft((current) => {
      const values = current[field];
      return {
        ...current,
        [field]: values.includes(id) ? values.filter((value) => value !== id) : [...values, id],
      };
    });
  }

  async function submitInvitation(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (selectedCompany === null) return;
    const data = new FormData(event.currentTarget);
    await run(async () => {
      const result = await inviteCompanyUser(
        selectedCompany.id,
        {
          displayName: formText(data, 'displayName'),
          email: formText(data, 'email'),
          role: formText(data, 'role') as CompanyUser['role'],
        },
        csrfToken,
      );
      setInvitationToken(result.invitationToken);
      setUsers(await listCompanyUsers(selectedCompany.id));
      event.currentTarget.reset();
    }, 'ユーザーを登録しました。招待トークンはこの画面で一度だけ確認できます。');
  }

  async function submitCompletion(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (selectedCompany === null || selectedProject === null) return;
    const data = new FormData(event.currentTarget);
    await run(async () => {
      const updated = await requestProjectCompletion(
        selectedCompany.id,
        selectedProject.id,
        formText(data, 'actualCompletedOn'),
        csrfToken,
      );
      setSelectedProject(updated);
      await refreshCompanyData(selectedCompany.id);
    }, '工事完了日を申請しました。One Colorの確定後に保存期限が設定されます。');
  }

  async function submitConfirmation(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (selectedCompany === null || selectedProject === null) return;
    const data = new FormData(event.currentTarget);
    await run(async () => {
      const actualCompletedOn = nullable(data.get('actualCompletedOn'));
      const updated = await confirmProjectCompletion(
        selectedCompany.id,
        selectedProject.id,
        {
          ...(actualCompletedOn === null ? {} : { actualCompletedOn }),
          confirmRetentionShortening: data.get('confirmRetentionShortening') === 'on',
          reason: nullable(data.get('reason')),
        },
        csrfToken,
      );
      setSelectedProject(updated);
      await refreshCompanyData(selectedCompany.id);
    }, '完了日と保存期限を確定しました。');
  }

  async function toggleAssignment(userId: string, assigned: boolean) {
    if (selectedCompany === null || selectedProject === null) return;
    await run(
      async () => {
        if (assigned) {
          await unassignProjectUser(selectedCompany.id, selectedProject.id, userId, csrfToken);
        } else {
          await assignProjectUser(selectedCompany.id, selectedProject.id, userId, csrfToken);
        }
        setAssignments(await listProjectAssignments(selectedCompany.id, selectedProject.id));
      },
      assigned ? '工事割当を解除しました。' : '現場担当者を割り当てました。',
    );
  }

  return (
    <section className="operations-shell">
      <div className="dashboard-toolbar card">
        <div>
          <p className="eyebrow">SIGNED IN</p>
          <h2>{user.displayName} さん</h2>
          <p className="muted">{user.email}</p>
        </div>
        <div className="toolbar-actions">
          {superAdmin && (
            <button className="secondary" onClick={() => beginCompany(null)} type="button">
              企業を追加
            </button>
          )}
          <button
            className="secondary"
            onClick={() => setAccountSettingsOpen((current) => !current)}
            type="button"
          >
            ログイン情報を変更
          </button>
          <button className="secondary" onClick={onLogout} type="button">
            ログアウト
          </button>
        </div>
      </div>

      {accountSettingsOpen && (
        <AccountSettingsPanel
          csrfToken={csrfToken}
          onClose={() => setAccountSettingsOpen(false)}
          onUpdated={onAccountUpdated}
          user={user}
        />
      )}

      {notice !== null && <div className="notice success">{notice}</div>}

      {companies.length === 0 && !superAdmin ? (
        <div className="card empty-state">
          利用可能な企業がありません。管理者へ所属状況を確認してください。
        </div>
      ) : (
        <>
          <div className="context-bar">
            <label>
              対象企業
              <select
                value={selectedCompanyId}
                onChange={(event) => setSelectedCompanyId(event.target.value)}
              >
                <option value="">企業を選択</option>
                {companies.map((company) => (
                  <option key={company.id} value={company.id}>
                    {company.name}（{company.companyCode}）
                  </option>
                ))}
              </select>
            </label>
            <nav aria-label="業務メニュー" className="tab-list">
              {(
                [
                  'projects',
                  'requests',
                  'delivery',
                  'users',
                  'company',
                  'content',
                  'electronic-profiles',
                  'masters',
                ] as const
              ).map((item) => {
                if (
                  (item === 'content' || item === 'electronic-profiles' || item === 'masters') &&
                  !superAdmin
                )
                  return null;
                if (item === 'users' && !canManageCompany) return null;
                const labels = {
                  company: '企業情報',
                  content: '安全コンテンツ',
                  delivery: '納品ページ',
                  'electronic-profiles': '電子納品profile',
                  masters: 'マスター',
                  projects: '工事',
                  requests: '月次依頼',
                  users: 'ユーザー',
                };
                return (
                  <button
                    className={tab === item ? 'tab active' : 'tab'}
                    key={item}
                    onClick={() => setTab(item)}
                    type="button"
                  >
                    {labels[item]}
                  </button>
                );
              })}
            </nav>
          </div>

          {tab === 'company' && superAdmin && (
            <CompanyForm
              busy={busy}
              draft={companyDraft}
              editing={selectedCompany !== null}
              onChange={setCompanyDraft}
              onArchive={
                selectedCompany === null
                  ? undefined
                  : (reason) =>
                      run(async () => {
                        await archiveCompany(
                          selectedCompany.id,
                          selectedCompany.version,
                          reason,
                          csrfToken,
                        );
                        setCompanyDraft(emptyCompany);
                        await refreshCompanies();
                      }, '企業をアーカイブしました。')
              }
              onSubmit={submitCompany}
            />
          )}

          {tab === 'company' && !superAdmin && selectedCompany !== null && (
            <article className="card detail-card">
              <p className="eyebrow">COMPANY</p>
              <h2>{selectedCompany.name}</h2>
              <dl className="detail-list">
                <div>
                  <dt>企業コード</dt>
                  <dd>{selectedCompany.companyCode}</dd>
                </div>
                <div>
                  <dt>状態</dt>
                  <dd>{selectedCompany.status}</dd>
                </div>
                <div>
                  <dt>契約開始日</dt>
                  <dd>{selectedCompany.contractStartedOn}</dd>
                </div>
                <div>
                  <dt>連絡先</dt>
                  <dd>{selectedCompany.contactEmail ?? '未登録'}</dd>
                </div>
              </dl>
            </article>
          )}

          {tab === 'projects' && selectedCompany !== null && (
            <div className="workspace-grid">
              <div className="stack">
                <div className="section-heading">
                  <div>
                    <p className="eyebrow">PROJECTS</p>
                    <h2>工事一覧</h2>
                  </div>
                  {canManageCompany && (
                    <button
                      className="secondary"
                      onClick={() => {
                        setEditingProject(null);
                        setProjectDraft(emptyProject);
                      }}
                      type="button"
                    >
                      新規工事
                    </button>
                  )}
                </div>
                {projects.length === 0 ? (
                  <div className="card empty-state">登録済みの工事はありません。</div>
                ) : (
                  projects.map((project) => (
                    <button
                      className="project-row"
                      key={project.id}
                      onClick={() => void selectProject(project.id)}
                      type="button"
                    >
                      <span>
                        <strong>{project.name}</strong>
                        <small>
                          {project.projectCode} · {labelForClient(project)}
                        </small>
                      </span>
                      <span className={`status-chip ${project.status.toLowerCase()}`}>
                        {project.status}
                      </span>
                    </button>
                  ))
                )}
                {selectedProject !== null && (
                  <>
                    <ProjectDetailPanel
                      assignments={assignments}
                      canManage={canManageCompany}
                      companyUsers={users}
                      onArchive={(reason) =>
                        run(async () => {
                          await archiveProject(
                            selectedCompany.id,
                            selectedProject.id,
                            selectedProject.version,
                            reason,
                            csrfToken,
                          );
                          setSelectedProject(null);
                          await refreshCompanyData(selectedCompany.id);
                        }, '工事をアーカイブしました。')
                      }
                      onAssignment={toggleAssignment}
                      onConfirm={submitConfirmation}
                      onEdit={() => void beginProjectEdit(selectedProject)}
                      onRequest={submitCompletion}
                      project={selectedProject}
                      superAdmin={superAdmin}
                    />
                    <RetentionPanel
                      canRetry={superAdmin}
                      companyId={selectedCompany.id}
                      csrfToken={csrfToken}
                      onError={onError}
                      projectId={selectedProject.id}
                    />
                    <ProjectArchivePanel
                      canGenerate={
                        superAdmin &&
                        selectedProject.deletionStatus !== 'DELETING' &&
                        selectedProject.deletionStatus !== 'DELETED'
                      }
                      companyId={selectedCompany.id}
                      csrfToken={csrfToken}
                      onError={onError}
                      projectId={selectedProject.id}
                    />
                  </>
                )}
              </div>
              {canManageCompany && (
                <ProjectForm
                  busy={busy}
                  draft={projectDraft}
                  editing={editingProject !== null}
                  equipmentTypes={equipmentTypes}
                  onChange={setProjectDraft}
                  onSubmit={submitProject}
                  onToggle={toggleSelection}
                  workTypes={workTypes}
                />
              )}
            </div>
          )}

          {tab === 'requests' && selectedCompany !== null && selectedProject === null && (
            <div className="card empty-state">
              「工事」タブで対象工事を選択してから、月次依頼を入力してください。
            </div>
          )}

          {tab === 'requests' && selectedCompany !== null && selectedProject !== null && (
            <TrainingRequestsPanel
              companyId={selectedCompany.id}
              csrfToken={csrfToken}
              equipmentTypes={equipmentTypes}
              onError={onError}
              project={selectedProject}
              user={user}
              workTypes={workTypes}
            />
          )}

          {tab === 'delivery' && selectedCompany !== null && (
            <DeliveryPortalPanel companyId={selectedCompany.id} onError={onError} />
          )}

          {tab === 'users' && selectedCompany !== null && canManageCompany && (
            <UsersPanel
              busy={busy}
              companyId={selectedCompany.id}
              csrfToken={csrfToken}
              invitationToken={invitationToken}
              onError={onError}
              onInvite={submitInvitation}
              onUsers={setUsers}
              users={users}
            />
          )}

          {tab === 'masters' && superAdmin && (
            <MastersPanel
              csrfToken={csrfToken}
              equipmentTypes={equipmentTypes}
              onEquipment={setEquipmentTypes}
              onError={onError}
              onWork={setWorkTypes}
              workTypes={workTypes}
            />
          )}

          {tab === 'content' && superAdmin && (
            <ContentLibraryPanel csrfToken={csrfToken} onError={onError} />
          )}

          {tab === 'electronic-profiles' && superAdmin && (
            <ElectronicDeliveryProfilesPanel csrfToken={csrfToken} onError={onError} />
          )}
        </>
      )}
    </section>
  );
}

function CompanyForm({
  busy,
  draft,
  editing,
  onChange,
  onArchive,
  onSubmit,
}: {
  busy: boolean;
  draft: CompanyInput;
  editing: boolean;
  onChange: (value: CompanyInput) => void;
  onArchive?: ((reason: string) => Promise<void>) | undefined;
  onSubmit: (event: FormEvent<HTMLFormElement>) => Promise<void>;
}) {
  const [archiveReason, setArchiveReason] = useState('');
  return (
    <form className="card form-grid" onSubmit={(event) => void onSubmit(event)}>
      <div className="form-heading">
        <p className="eyebrow">COMPANY</p>
        <h2>{editing ? '企業情報を編集' : '企業を登録'}</h2>
      </div>
      <label>
        企業コード
        <input
          required
          value={draft.companyCode}
          onChange={(event) => onChange({ ...draft, companyCode: event.target.value })}
        />
      </label>
      <label>
        会社名
        <input
          required
          value={draft.name}
          onChange={(event) => onChange({ ...draft, name: event.target.value })}
        />
      </label>
      <label>
        会社名カナ
        <input
          value={draft.nameKana ?? ''}
          onChange={(event) => onChange({ ...draft, nameKana: event.target.value || null })}
        />
      </label>
      <label>
        契約開始日
        <input
          required
          type="date"
          value={draft.contractStartedOn}
          onChange={(event) => onChange({ ...draft, contractStartedOn: event.target.value })}
        />
      </label>
      <label>
        担当者名
        <input
          value={draft.contactName ?? ''}
          onChange={(event) => onChange({ ...draft, contactName: event.target.value || null })}
        />
      </label>
      <label>
        連絡先メール
        <input
          type="email"
          value={draft.contactEmail ?? ''}
          onChange={(event) => onChange({ ...draft, contactEmail: event.target.value || null })}
        />
      </label>
      <label>
        電話番号
        <input
          value={draft.phone ?? ''}
          onChange={(event) => onChange({ ...draft, phone: event.target.value || null })}
        />
      </label>
      <label>
        状態
        <select
          value={draft.status}
          onChange={(event) =>
            onChange({ ...draft, status: event.target.value as CompanyInput['status'] })
          }
        >
          <option value="ACTIVE">有効</option>
          <option value="SUSPENDED">停止</option>
        </select>
      </label>
      <label className="span-2">
        所在地
        <textarea
          value={draft.address ?? ''}
          onChange={(event) => onChange({ ...draft, address: event.target.value || null })}
        />
      </label>
      <label className="span-2">
        備考
        <textarea
          value={draft.remarks ?? ''}
          onChange={(event) => onChange({ ...draft, remarks: event.target.value || null })}
        />
      </label>
      <button disabled={busy} type="submit">
        {editing ? '企業情報を保存' : '企業を登録'}
      </button>
      {onArchive !== undefined && (
        <div className="danger-zone span-2">
          <label>
            アーカイブ理由
            <input
              value={archiveReason}
              onChange={(event) => setArchiveReason(event.target.value)}
            />
          </label>
          <button
            className="danger"
            disabled={archiveReason.trim().length < 10}
            onClick={() => void onArchive(archiveReason)}
            type="button"
          >
            企業をアーカイブ
          </button>
        </div>
      )}
    </form>
  );
}

function ProjectForm({
  busy,
  draft,
  editing,
  equipmentTypes,
  onChange,
  onSubmit,
  onToggle,
  workTypes,
}: {
  busy: boolean;
  draft: ProjectInput;
  editing: boolean;
  equipmentTypes: CatalogItem[];
  onChange: (value: ProjectInput) => void;
  onSubmit: (event: FormEvent<HTMLFormElement>) => Promise<void>;
  onToggle: (field: 'equipmentTypeIds' | 'workTypeIds', id: string) => void;
  workTypes: CatalogItem[];
}) {
  return (
    <form className="card project-form" onSubmit={(event) => void onSubmit(event)}>
      <p className="eyebrow">PROJECT EDITOR</p>
      <h2>{editing ? '工事を編集' : '工事を登録'}</h2>
      <label>
        工事コード
        <input
          required
          value={draft.projectCode}
          onChange={(event) => onChange({ ...draft, projectCode: event.target.value })}
        />
      </label>
      <label>
        工事名
        <input
          required
          value={draft.name}
          onChange={(event) => onChange({ ...draft, name: event.target.value })}
        />
      </label>
      <label>
        工事番号
        <input
          value={draft.contractNumber ?? ''}
          onChange={(event) => onChange({ ...draft, contractNumber: event.target.value || null })}
        />
      </label>
      <label>
        発注者区分
        <select
          value={draft.clientProfileType}
          onChange={(event) =>
            onChange({
              ...draft,
              clientProfileType: event.target.value as ProjectInput['clientProfileType'],
              municipalityName: null,
            })
          }
        >
          <option value="HOKKAIDO_DEVELOPMENT_BUREAU">北海道開発局</option>
          <option value="HOKKAIDO_CONSTRUCTION_DEPARTMENT">北海道建設部</option>
          <option value="MUNICIPALITY">市町村</option>
        </select>
      </label>
      {draft.clientProfileType === 'MUNICIPALITY' && (
        <label>
          市町村名
          <input
            required
            value={draft.municipalityName ?? ''}
            onChange={(event) => onChange({ ...draft, municipalityName: event.target.value })}
          />
        </label>
      )}
      <div className="date-pair">
        <label>
          工期開始日
          <input
            required
            type="date"
            value={draft.plannedStartOn}
            onChange={(event) => onChange({ ...draft, plannedStartOn: event.target.value })}
          />
        </label>
        <label>
          完了予定日
          <input
            required
            type="date"
            value={draft.plannedCompleteOn}
            onChange={(event) => onChange({ ...draft, plannedCompleteOn: event.target.value })}
          />
        </label>
      </div>
      <label>
        予定回数（空欄は自動計算）
        <input
          min="1"
          max="120"
          type="number"
          value={draft.expectedTrainingCount ?? ''}
          onChange={(event) => {
            if (event.target.value === '') {
              const withoutExpected: ProjectInput = { ...draft };
              delete withoutExpected.expectedTrainingCount;
              onChange(withoutExpected);
            } else {
              onChange({ ...draft, expectedTrainingCount: Number(event.target.value) });
            }
          }}
        />
      </label>
      <fieldset>
        <legend>工種</legend>
        <div className="check-grid">
          {workTypes
            .filter((item) => item.status === 'ACTIVE')
            .map((item) => (
              <label className="check-label" key={item.id}>
                <input
                  checked={draft.workTypeIds.includes(item.id)}
                  onChange={() => onToggle('workTypeIds', item.id)}
                  type="checkbox"
                />
                {item.name}
              </label>
            ))}
        </div>
      </fieldset>
      <fieldset>
        <legend>使用機械</legend>
        <div className="check-grid">
          {equipmentTypes
            .filter((item) => item.status === 'ACTIVE')
            .map((item) => (
              <label className="check-label" key={item.id}>
                <input
                  checked={draft.equipmentTypeIds.includes(item.id)}
                  onChange={() => onToggle('equipmentTypeIds', item.id)}
                  type="checkbox"
                />
                {item.name}
              </label>
            ))}
        </div>
      </fieldset>
      <label>
        現場代理人
        <input
          value={draft.siteManagerName ?? ''}
          onChange={(event) => onChange({ ...draft, siteManagerName: event.target.value || null })}
        />
      </label>
      <label>
        主任技術者
        <input
          value={draft.chiefEngineerName ?? ''}
          onChange={(event) =>
            onChange({ ...draft, chiefEngineerName: event.target.value || null })
          }
        />
      </label>
      <label>
        監理技術者
        <input
          value={draft.supervisingEngineerName ?? ''}
          onChange={(event) =>
            onChange({ ...draft, supervisingEngineerName: event.target.value || null })
          }
        />
      </label>
      <label>
        状態
        <select
          value={draft.status}
          onChange={(event) =>
            onChange({ ...draft, status: event.target.value as ProjectInput['status'] })
          }
        >
          <option value="PLANNED">計画中</option>
          <option value="ACTIVE">稼働中</option>
          <option value="SUSPENDED">停止</option>
        </select>
      </label>
      <label>
        主な工事内容
        <textarea
          value={draft.mainWorkSummary ?? ''}
          onChange={(event) => onChange({ ...draft, mainWorkSummary: event.target.value || null })}
        />
      </label>
      <label>
        備考
        <textarea
          value={draft.remarks ?? ''}
          onChange={(event) => onChange({ ...draft, remarks: event.target.value || null })}
        />
      </label>
      <button disabled={busy || draft.workTypeIds.length === 0} type="submit">
        {editing ? '変更を保存' : '工事を登録'}
      </button>
    </form>
  );
}

function ProjectDetailPanel({
  assignments,
  canManage,
  companyUsers,
  onArchive,
  onAssignment,
  onConfirm,
  onEdit,
  onRequest,
  project,
  superAdmin,
}: {
  assignments: ProjectAssignment[];
  canManage: boolean;
  companyUsers: CompanyUser[];
  onArchive: (reason: string) => Promise<void>;
  onAssignment: (userId: string, assigned: boolean) => Promise<void>;
  onConfirm: (event: FormEvent<HTMLFormElement>) => Promise<void>;
  onEdit: () => void;
  onRequest: (event: FormEvent<HTMLFormElement>) => Promise<void>;
  project: Project;
  superAdmin: boolean;
}) {
  const assigned = new Set(assignments.map((item) => item.userId));
  const [archiveReason, setArchiveReason] = useState('');
  return (
    <article className="card detail-card">
      <div className="section-heading">
        <div>
          <p className="eyebrow">PROJECT DETAIL</p>
          <h2>{project.name}</h2>
        </div>
        {canManage && project.status !== 'COMPLETED' && project.status !== 'COMPLETION_PENDING' && (
          <button className="secondary" onClick={onEdit} type="button">
            編集
          </button>
        )}
      </div>
      <dl className="detail-list">
        <div>
          <dt>発注者</dt>
          <dd>{labelForClient(project)}</dd>
        </div>
        <div>
          <dt>工期</dt>
          <dd>
            {project.plannedStartOn}〜{project.plannedCompleteOn}
          </dd>
        </div>
        <div>
          <dt>予定回数</dt>
          <dd>{project.expectedTrainingCount}回</dd>
        </div>
        <div>
          <dt>発行済み / 残り</dt>
          <dd>
            {project.issuedTrainingCount ?? 0} /{' '}
            {project.remainingTrainingCount ?? project.expectedTrainingCount}
          </dd>
        </div>
        <div>
          <dt>保存期限</dt>
          <dd>{project.retentionExpiresOn ?? '完了日未確定'}</dd>
        </div>
      </dl>
      {canManage && (
        <>
          <h3>現場担当者</h3>
          <div className="assignment-list">
            {companyUsers
              .filter((member) => member.role === 'SITE_USER' && member.status === 'ACTIVE')
              .map((member) => (
                <label className="check-label" key={member.userId}>
                  <input
                    checked={assigned.has(member.userId)}
                    onChange={() => void onAssignment(member.userId, assigned.has(member.userId))}
                    type="checkbox"
                  />
                  {member.displayName}
                </label>
              ))}
          </div>
        </>
      )}
      {canManage && project.status !== 'COMPLETED' && (
        <form className="inline-form" onSubmit={(event) => void onRequest(event)}>
          <label>
            実完了日
            <input name="actualCompletedOn" required type="date" />
          </label>
          <button type="submit">完了日を申請</button>
        </form>
      )}
      {superAdmin &&
        (project.requestedActualCompletedOn !== null || project.actualCompletedOn !== null) && (
          <form className="confirmation-form" onSubmit={(event) => void onConfirm(event)}>
            <h3>One Color確認</h3>
            <label>
              確定日
              <input
                defaultValue={project.requestedActualCompletedOn ?? project.actualCompletedOn ?? ''}
                name="actualCompletedOn"
                type="date"
              />
            </label>
            <label className="check-label">
              <input name="confirmRetentionShortening" type="checkbox" />
              保存期限の短縮を明示確認
            </label>
            <label>
              確認理由
              <textarea name="reason" placeholder="短縮時は10文字以上必須" />
            </label>
            <button type="submit">完了日と保存期限を確定</button>
          </form>
        )}
      {canManage && (
        <div className="danger-zone">
          <label>
            アーカイブ理由
            <input
              value={archiveReason}
              onChange={(event) => setArchiveReason(event.target.value)}
            />
          </label>
          <button
            className="danger"
            disabled={archiveReason.trim().length < 10}
            onClick={() => void onArchive(archiveReason)}
            type="button"
          >
            工事をアーカイブ
          </button>
        </div>
      )}
    </article>
  );
}

function UsersPanel({
  busy,
  companyId,
  csrfToken,
  invitationToken,
  onError,
  onInvite,
  onUsers,
  users,
}: {
  busy: boolean;
  companyId: string;
  csrfToken: string;
  invitationToken: string | null;
  onError: (error: unknown) => void;
  onInvite: (event: FormEvent<HTMLFormElement>) => Promise<void>;
  onUsers: (users: CompanyUser[]) => void;
  users: CompanyUser[];
}) {
  async function change(user: CompanyUser, patch: Partial<Pick<CompanyUser, 'role' | 'status'>>) {
    try {
      await updateCompanyUser(
        companyId,
        user.userId,
        {
          role: patch.role ?? user.role,
          status: patch.status ?? user.status,
        },
        csrfToken,
      );
      onUsers(await listCompanyUsers(companyId));
    } catch (error) {
      onError(error);
    }
  }
  return (
    <div className="workspace-grid">
      <form className="card project-form" onSubmit={(event) => void onInvite(event)}>
        <p className="eyebrow">INVITATION</p>
        <h2>ユーザー招待</h2>
        <label>
          表示名
          <input name="displayName" required />
        </label>
        <label>
          メール
          <input name="email" required type="email" />
        </label>
        <label>
          ロール
          <select name="role">
            <option value="SITE_USER">現場担当者</option>
            <option value="COMPANY_ADMIN">企業管理者</option>
          </select>
        </label>
        <button disabled={busy} type="submit">
          招待を作成
        </button>
        {invitationToken !== null && (
          <div className="token-box">
            <strong>一度だけ表示される招待トークン</strong>
            <code>{invitationToken}</code>
            <small>安全な別経路で本人へ渡してください。</small>
          </div>
        )}
      </form>
      <div className="card">
        <p className="eyebrow">MEMBERS</p>
        <h2>所属ユーザー</h2>
        <div className="member-list">
          {users.map((member) => (
            <div className="member-row" key={member.userId}>
              <span>
                <strong>{member.displayName}</strong>
                <small>{member.email}</small>
              </span>
              <select
                aria-label={`${member.displayName}のロール`}
                value={member.role}
                onChange={(event) =>
                  void change(member, { role: event.target.value as CompanyUser['role'] })
                }
              >
                <option value="COMPANY_ADMIN">企業管理者</option>
                <option value="SITE_USER">現場担当者</option>
              </select>
              <select
                aria-label={`${member.displayName}の状態`}
                disabled={member.status === 'INVITED'}
                value={member.status}
                onChange={(event) =>
                  void change(member, { status: event.target.value as CompanyUser['status'] })
                }
              >
                <option value="INVITED">招待中</option>
                <option value="ACTIVE">有効</option>
                <option value="SUSPENDED">停止</option>
              </select>
              <button
                className="danger ghost"
                onClick={() =>
                  void removeCompanyUser(
                    companyId,
                    member.userId,
                    '管理者による企業所属の解除処理',
                    csrfToken,
                  )
                    .then(() => listCompanyUsers(companyId))
                    .then(onUsers)
                    .catch(onError)
                }
                type="button"
              >
                解除
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function MastersPanel({
  csrfToken,
  equipmentTypes,
  onEquipment,
  onError,
  onWork,
  workTypes,
}: {
  csrfToken: string;
  equipmentTypes: CatalogItem[];
  onEquipment: (items: CatalogItem[]) => void;
  onError: (error: unknown) => void;
  onWork: (items: CatalogItem[]) => void;
  workTypes: CatalogItem[];
}) {
  async function submit(event: FormEvent<HTMLFormElement>, kind: 'equipment-types' | 'work-types') {
    event.preventDefault();
    const data = new FormData(event.currentTarget);
    try {
      await createCatalogItem(
        kind,
        { code: formText(data, 'code'), name: formText(data, 'name') },
        csrfToken,
      );
      const items = await listCatalog(kind);
      if (kind === 'work-types') onWork(items);
      else onEquipment(items);
      event.currentTarget.reset();
    } catch (error) {
      onError(error);
    }
  }
  function list(
    kind: 'equipment-types' | 'work-types',
    items: CatalogItem[],
    setter: (items: CatalogItem[]) => void,
  ) {
    return (
      <article className="card">
        <h2>{kind === 'work-types' ? '工種' : '使用機械'}</h2>
        <form className="inline-form" onSubmit={(event) => void submit(event, kind)}>
          <label>
            コード
            <input name="code" required />
          </label>
          <label>
            名称
            <input name="name" required />
          </label>
          <button type="submit">追加</button>
        </form>
        <div className="member-list">
          {items.map((item) => (
            <div className="member-row" key={item.id}>
              <span>
                <strong>{item.name}</strong>
                <small>{item.code}</small>
              </span>
              <button
                className="secondary"
                onClick={() =>
                  void updateCatalogItem(
                    kind,
                    item.id,
                    { name: item.name, status: item.status === 'ACTIVE' ? 'INACTIVE' : 'ACTIVE' },
                    csrfToken,
                  )
                    .then(() => listCatalog(kind))
                    .then(setter)
                    .catch(onError)
                }
                type="button"
              >
                {item.status === 'ACTIVE' ? '無効化' : '有効化'}
              </button>
            </div>
          ))}
        </div>
      </article>
    );
  }
  return (
    <div className="workspace-grid">
      {list('work-types', workTypes, onWork)}
      {list('equipment-types', equipmentTypes, onEquipment)}
    </div>
  );
}
